import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	type Signal,
	untracked,
	viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	NavigationCancel,
	NavigationCancellationCode,
	NavigationEnd,
	NavigationError,
	NavigationSkipped,
	NavigationStart,
	PRIMARY_OUTLET,
	Router,
	RouterOutlet,
} from '@angular/router';
import { filter, map } from 'rxjs';

import { UiFeatureFlagKey } from '@efficiently/feature-flags-core';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { FeatureFlagsService } from '@efficiently/client-core-feature-flags';
import {
	APP_ROUTES,
	APP_SECTION_ID,
	APP_SECTIONS,
	type AppSectionDefinition,
	type AppSectionId,
	PAGES_ROUTES,
} from '@efficiently/client-core-routing';
import {
	APP_SHELL_PANEL_MODE,
	AppPanelTabsComponent,
	AppRailComponent,
	AppSectionSwitcherComponent,
	AppShellPanelStore,
	AppShellPanelTabsStore,
	AppShellRegistry,
	AppSidePanelComponent,
	AppTabStripComponent,
} from '@efficiently/client-ui-app-shell';
import {
	ClarityFlyInMaxWidthDirective,
	ClarityFlyInTopEdgeDirective,
	ClarityTopBarSlot,
} from '@efficiently/client-ui-clarity-overlays';
import { EFF_SIDEBAR_SHELL } from '@efficiently/client-ui-kit';
import { CLARITY_DOMAIN_ROUTE_SEGMENT } from '@efficiently/client-util-clarity';
import { uuidToShort } from '@efficiently/client-util-helpers';

import { HeaderComponent } from '@ds/core/components/header/header.component';
import { HeaderContextTitleService } from '@ds/core/components/header/header-context-title.service';
import { HeaderTitleComponent } from '@ds/core/components/header/header-title/header-title.component';
import { CLIENT_PORTAL_ROUTES_CONST } from '@ds/pages/client-app-portal/client-portal-routes.const';
import { PlatformAdminTabsStore } from '@ds/pages/platform-admin/platform-admin-tabs.store';
import { SidenavWrapperComponent } from '@ds/shared/modules';

import { APP_RAIL_BUTTONS } from './constants/app-rail-buttons.constant';
import { PANEL_DEFAULTS } from './constants/panel-defaults.constant';
import { DomainNavigatorComponent } from './domain-navigator/domain-navigator.component';
import { DomainNavigatorStore } from './domain-navigator/domain-navigator.store';
import { DomainPlaceStore } from './domain-place/domain-place.store';
import { DOMAIN_PLACE_KIND } from './domain-place/domain-place-kind.constant';
import type { ProjectDomainLink } from './project-domain-link/project-domain-link.model';
import { ProjectDomainLinkStore } from './project-domain-link/project-domain-link.store';
import { DefaultProjectSurfaceAccessService } from './surface-access/default-project-surface-access.service';
import { ProjectSurfaceAccessService } from './surface-access/project-surface-access.service';
import { SubscriptionRenewalPromptService } from './user-menu/subscription-renewal-prompt.service';
import { UserMenuComponent } from './user-menu/user-menu.component';

const CONTROL_CENTER_ONLY: ReadonlySet<string> = new Set([APP_SECTION_ID.CONTROL_CENTER]);

/**
 * The container the CDK renders menus, dialogs and tooltips into. A click there belongs to whatever the
 * open panel opened, so it never counts as a click outside the panel.
 */
const CDK_OVERLAY_CONTAINER_CLASS = 'cdk-overlay-container';

/**
 * The leading segments of every project-scoped Supply URL, built from the route table rather than
 * written out: the same two segments are read by the Supply section's own route table and by its tab
 * strip, and a fourth hand-written copy would drift silently — a mismatch renders an empty page
 * instead of throwing. `APP_ROUTES` paths carry NO leading slash while `Router.url` does, which is
 * why both sides are reduced to segment arrays before they are compared.
 */
const SUPPLY_PROJECT_SEGMENTS: readonly string[] = [APP_ROUTES.SUPPLY.path, APP_ROUTES.SUPPLY_PROJECT.path];

/**
 * Splits a path into its URL segments, dropping the leading slash a `Router.url` always carries and
 * the one a route-table path never does. Mirrors the reader in `AppShellRegistry` deliberately, so a
 * later reader finds one shape rather than two.
 */
function toSegments(path: string): readonly string[] {
	return path.split('/').filter(segment => segment.length > 0);
}

/**
 * The same leading segments for a project-scoped Design URL, read off the route table for the same
 * reason. It is one segment rather than two because `APP_ROUTES.PAGES.path` is empty and contributes
 * nothing to the URL.
 */
const DESIGN_PROJECT_SEGMENTS: readonly string[] = toSegments(PAGES_ROUTES.PROJECTS.fullPath());

/** The leading segments of a project-scoped Client Portal URL, read off the section's own route table. */
const CLIENT_PORTAL_PROJECT_SEGMENTS: readonly string[] = toSegments(CLIENT_PORTAL_ROUTES_CONST.ROOMS.fullPath(''));

/** Drops the query string and the fragment, neither of which takes part in matching. */
function toPath(url: string): string {
	const [pathWithQuery] = url.split('#');
	const [path] = pathWithQuery.split('?');

	return path;
}

/**
 * The project id a URL carries under the given leading segments, or `null` when the URL is not under
 * them or stops at the root they name — `/supply` is the section's landing page, not a project.
 */
function readProjectIdUnder(prefix: readonly string[], urlSegments: readonly string[]): string | null {
	const isUnderPrefix = prefix.every((segment, index) => urlSegments[index] === segment);

	return isUnderPrefix && urlSegments.length > prefix.length ? urlSegments[prefix.length] : null;
}

const CLARITY_DOMAIN_SEGMENTS: readonly string[] = [APP_ROUTES.CLARITY.path, CLARITY_DOMAIN_ROUTE_SEGMENT];

const URL_SEGMENT_SEPARATOR = '/';

const NO_PROJECT_LANDING_PATHS: ReadonlyMap<string, string> = new Map();

function toControlCenterPath(domainId: string): string {
	return [...CLARITY_DOMAIN_SEGMENTS, domainId].join(URL_SEGMENT_SEPARATOR);
}

function toProjectLandingPaths(link: ProjectDomainLink): ReadonlyMap<string, string> {
	const landingPaths = new Map<string, string>();

	if (link.domainId !== null) {
		landingPaths.set(APP_SECTION_ID.CONTROL_CENTER, toControlCenterPath(link.domainId));
	}

	if (link.projectId !== null) {
		const projectShortId = uuidToShort(link.projectId);

		landingPaths.set(
			APP_SECTION_ID.DESIGN,
			[...DESIGN_PROJECT_SEGMENTS, projectShortId].join(URL_SEGMENT_SEPARATOR),
		);
		landingPaths.set(
			APP_SECTION_ID.SUPPLY,
			[...SUPPLY_PROJECT_SEGMENTS, projectShortId].join(URL_SEGMENT_SEPARATOR),
		);
		landingPaths.set(
			APP_SECTION_ID.CLIENT_PORTAL,
			[...CLIENT_PORTAL_PROJECT_SEGMENTS, projectShortId].join(URL_SEGMENT_SEPARATOR),
		);
	}

	return landingPaths;
}

function readProjectSurfaceId(urlSegments: readonly string[]): AppSectionId | null {
	if (readProjectIdUnder(SUPPLY_PROJECT_SEGMENTS, urlSegments) !== null) {
		return APP_SECTION_ID.SUPPLY;
	}

	if (readProjectIdUnder(CLIENT_PORTAL_PROJECT_SEGMENTS, urlSegments) !== null) {
		return APP_SECTION_ID.CLIENT_PORTAL;
	}

	return readProjectIdUnder(DESIGN_PROJECT_SEGMENTS, urlSegments) === null ? null : APP_SECTION_ID.DESIGN;
}

/**
 * The project a URL is inside, whichever section draws it, or `null` outside a project.
 *
 * The domain navigator opens a project by navigating, so the id is what tells a pick from every other
 * navigation: a tab click and a section switch leave it alone.
 */
function readProjectId(urlSegments: readonly string[]): string | null {
	return (
		readProjectIdUnder(SUPPLY_PROJECT_SEGMENTS, urlSegments) ??
		readProjectIdUnder(CLIENT_PORTAL_PROJECT_SEGMENTS, urlSegments) ??
		readProjectIdUnder(DESIGN_PROJECT_SEGMENTS, urlSegments)
	);
}

function isSectionEnabled(
	section: AppSectionDefinition,
	isSupplyEnabled: boolean,
	isAwaitingPlatformLink: boolean,
): boolean {
	if (isAwaitingPlatformLink && section.id !== APP_SECTION_ID.CONTROL_CENTER) {
		return false;
	}

	return section.id === APP_SECTION_ID.SUPPLY ? isSupplyEnabled : section.enabled;
}

/**
 * The one shell above every platform section: the application header carrying the section
 * switcher, the active section's tab strip, the persistent left icon rail, the panel that rail
 * opens, the domain navigator floating over both, and the outlet the sections render into.
 *
 * It sits on the route parent that carries `PagesCanLoadGuard`, so the platform login gates the
 * whole shell once rather than the Design section alone — which is what puts a Control Center URL
 * inside the application frame and behind the platform session.
 *
 * **The content region is composed here, not configured.** Five shapes in the template are
 * load-bearing:
 *
 * - The rail is a SIBLING of `<eff-sidebar-shell>`, never a child of its aside. The aside is a
 *   vertical stack with pinned header and footer slots, so a rail and a panel side by side cannot
 *   both live in it — and the rail has to survive the aside's removal.
 * - "Closed" is the ABSENCE of the aside, not a narrowed one. The kit's collapse state is an
 *   icon-rail width, and this shell already renders a rail, so collapsing would stand a second
 *   empty strip beside it. Not projecting the aside also keeps a closed panel out of the
 *   accessibility tree without an `inert` attribute and needs no token override. Hence
 *   `[collapsible]` bound `false`, and the kit's controlled collapse input bound nowhere.
 * - The tab strip is INSIDE `<eff-sidebar-shell-main>`, not above the body row, so that it is
 *   always exactly the width of main and does not slide sideways when the panel opens. It shares
 *   one chrome band with `<eff-app-panel-tabs>` in the aside's pinned header slot: two elements,
 *   because they are two columns of the kit's grid, but one fill and one height, held together by
 *   the `chromeBand` mixin and the `--eff-comp-app-chrome-band-*` tokens rather than by two files
 *   agreeing. Main is the page scroll-host, so the stylesheet pins the strip to its top edge and
 *   pays the matching `scroll-padding-block-start`.
 * - `:host` is a bounded flex column rather than a block. `<eff-sidebar-shell>` is itself
 *   `block-size: 100%`, so the header keeps its intrinsic size and the body row takes the rest —
 *   the stylesheet carries the rest of that argument.
 * - The aside is projected only in `Push` mode; `Overlay` mode projects no aside at all and instead
 *   renders a separate absolutely-positioned container as a sibling of `<eff-sidebar-shell>`. The
 *   kit's aside is a grid area beside the main slot, and a grid area cannot overlap its sibling
 *   without overriding the kit's own track definition from a consumer — which this shell must not
 *   do, since that would affect every other `<eff-sidebar-shell>` in the application. Not
 *   projecting the aside in overlay mode is the same "closed" shape the kit already handles, so
 *   overlay mode costs the kit nothing, and exactly one `<eff-app-side-panel>` ever renders because
 *   the two branches are mutually exclusive by construction.
 *
 * Still not here: the overlay library, which is `EFF-25624`, and the message and user sockets,
 * which stay in `PagesComponent` deliberately — they do not run on a `/clarity` URL today either,
 * so leaving them is not a regression while hoisting them would be feature behaviour no requirement
 * asks for.
 */
@Component({
	selector: 'ds-platform-shell',
	templateUrl: './platform-shell.component.html',
	styleUrl: './platform-shell.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'(document:pointerdown)': 'rememberPressOrigin($event)',
		'(document:click)': 'closeOverlayPanelOnOutsideClick($event)',
	},
	imports: [
		AppPanelTabsComponent,
		AppRailComponent,
		AppSectionSwitcherComponent,
		AppSidePanelComponent,
		AppTabStripComponent,
		CdkPortalOutlet,
		ClarityFlyInMaxWidthDirective,
		ClarityFlyInTopEdgeDirective,
		DomainNavigatorComponent,
		HeaderComponent,
		HeaderTitleComponent,
		RouterOutlet,
		SidenavWrapperComponent,
		TestIdDirective,
		UserMenuComponent,
		...EFF_SIDEBAR_SHELL,
	],
	providers: [
		{ provide: ProjectSurfaceAccessService, useClass: DefaultProjectSurfaceAccessService },
		SubscriptionRenewalPromptService,
	],
})
export class PlatformShellComponent {
	private readonly registry = inject(AppShellRegistry);
	private readonly panelStore = inject(AppShellPanelStore);
	private readonly panelTabs = inject(AppShellPanelTabsStore);
	private readonly navigator = inject(DomainNavigatorStore);
	private readonly domainPlaceStore = inject(DomainPlaceStore);
	private readonly surfaceAccess = inject(ProjectSurfaceAccessService);
	private readonly projectDomainLink = inject(ProjectDomainLinkStore);
	private readonly router = inject(Router);
	private readonly rail = viewChild.required('rail', { read: ElementRef<HTMLElement> });
	private readonly overlayPanel = viewChild('overlayPanel', { read: ElementRef<HTMLElement> });
	private readonly sectionSwitcher = viewChild.required('sectionSwitcher', { read: ElementRef<HTMLElement> });
	private hasPressStartedOutsidePanel = false;

	/**
	 * `FeatureFlagsService.enabled` hands back a `Signal<boolean>`, memoised per key, so this is a
	 * field rather than a call site: it composes straight into the `computed` below and re-derives
	 * the switcher the moment the flag's value arrives or changes.
	 */
	private readonly isSupplyProjectCreationEnabled = inject(FeatureFlagsService).enabled(
		UiFeatureFlagKey.SupplyProjectCreation,
	);

	// Read off the navigation stream with the current URL as the initial value, the same shape
	// `AppShellRegistry` uses: this component outlives every navigation inside the shell, so a single
	// constructor snapshot would pin the derived list to whatever URL happened to be first.
	private readonly urlSegments = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => toSegments(toPath(this.router.url))),
		),
		{ initialValue: toSegments(toPath(this.router.url)) },
	);

	private readonly isOnProjectUrl: Signal<boolean> = computed(() => readProjectId(this.urlSegments()) !== null);

	private readonly isOnProjectDomain: Signal<boolean> = computed(
		() => this.domainPlaceStore.place()?.kind === DOMAIN_PLACE_KIND.PROJECT,
	);

	private readonly isAwaitingPlatformLink: Signal<boolean> = computed(
		() =>
			!this.isOnProjectUrl() &&
			this.isOnProjectDomain() &&
			(this.projectDomainLink.current()?.projectId ?? null) === null,
	);

	private readonly visibleSectionIds: Signal<ReadonlySet<string>> = computed(() => {
		if (this.isOnProjectUrl() || this.isOnProjectDomain()) {
			return new Set<string>([APP_SECTION_ID.CONTROL_CENTER, ...this.surfaceAccess.grantedSurfaceIds()]);
		}

		return CONTROL_CENTER_ONLY;
	});

	private readonly projectLandingPaths: Signal<ReadonlyMap<string, string>> = computed(() => {
		const link = this.projectDomainLink.current();

		return link === null ? NO_PROJECT_LANDING_PATHS : toProjectLandingPaths(link);
	});

	private readonly openedProjectSurfaceId: Signal<AppSectionId | null> = computed(() =>
		readProjectSurfaceId(this.urlSegments()),
	);

	/**
	 * The list the switcher actually renders, derived from the URL, the feature flag, the domain place,
	 * the surfaces the person has on a project and the project the person is on — and from nothing the
	 * registry holds. Reading `registry.sections()` or `registry.activeSectionId()` here would make this
	 * derivation depend on its own write.
	 *
	 * `APP_SECTIONS` is the BASE: `supply` takes the flag, every other entry keeps the value it declares
	 * — so `control-center`, `design` and `client-portal` stay enabled and `build` stays disabled
	 * without being named here. On a project, an entry lands on the same project when its id is known
	 * (EFF-26724), and on its declared landing otherwise.
	 *
	 * Control Center is visible everywhere. The surfaces join it on a project URL and on a domain whose
	 * kind is `project`. On a project domain not joined to its platform project yet they are visible but
	 * disabled, and enabled once the link exists. Everywhere else — any other Clarity domain, a section's
	 * landing and the account and admin pages the user menu opens — Control Center stands alone, so no
	 * tab can open another project's page.
	 */
	private readonly switcherSections: Signal<readonly AppSectionDefinition[]> = computed(() => {
		const isSupplyEnabled = this.isSupplyProjectCreationEnabled();
		const isAwaitingPlatformLink = this.isAwaitingPlatformLink();
		const visibleSectionIds = this.visibleSectionIds();
		const projectLandingPaths = this.projectLandingPaths();

		return APP_SECTIONS.map(section => {
			const visible = visibleSectionIds.has(section.id);
			const enabled = isSectionEnabled(section, isSupplyEnabled, isAwaitingPlatformLink);
			const landingPath = projectLandingPaths.get(section.id) ?? section.landingPath;

			return { ...section, enabled, visible, landingPath };
		});
	});

	/**
	 * The Platform Administrator row, published by that group's route host while it is open.
	 *
	 * Read here rather than in `PagesComponent`, which rendered it until this change: the row is
	 * section chrome, and section chrome belongs to the shell that has sections. The build without
	 * them never names this component, so it never renders the row.
	 */
	protected readonly platformAdminTabs = inject(PlatformAdminTabsStore).tabs;

	/**
	 * The header's centre text, owned by {@link HeaderContextTitleService} rather than computed here —
	 * this shell only projects it. The build without the Control Center never names either.
	 */
	protected readonly headerTitle: Signal<string | null> = inject(HeaderContextTitleService).title;

	protected readonly topBarPortal = inject(ClarityTopBarSlot).portal;

	protected readonly isPanelOpen = this.panelStore.isOpen;
	/**
	 * The side panel's width, read by both modes: the pushing aside through the shell's `asideWidth`,
	 * the overlay through `--ds-platform-shell-panel-width`. Bound on those two elements only, never on
	 * an ancestor of the page, so a drag of the chat's resize handle restyles the panel and the shell
	 * track rather than every routed page beneath them. The `100%` is the reflow guard (WCAG 1.4.10).
	 */
	protected readonly panelWidth: Signal<`calc(${string})`> = computed(
		() => `calc(min(var(--eff-sys-layout-drawer-width) * ${this.panelStore.panelScale()}, 100%))` as const,
	);
	protected readonly APP_SHELL_PANEL_MODE = APP_SHELL_PANEL_MODE;

	/**
	 * Which container the open panel renders through: push, so the page narrows beside the panel,
	 * unless the page draws a sidebar of its own for the panel to cover. The same for every button.
	 */
	protected readonly panelRenderMode = this.panelStore.panelMode;

	protected readonly hasPanelTabs: Signal<boolean> = computed(() => this.panelTabs.tabs().length > 0);

	/** The extra-details panel's inline start: the open side panel's edge, else the rail's. */
	protected readonly extraDetailsStart: Signal<string> = computed(() =>
		this.isPanelOpen()
			? `calc(var(--eff-sys-layout-sidebar-width-collapsed) + ${this.panelWidth()})`
			: 'var(--eff-sys-layout-sidebar-width-collapsed)',
	);

	constructor() {
		// Here and not in the user menu: the rail and the domain navigator's footer each draw one.
		inject(SubscriptionRenewalPromptService);

		// Eagerly, from the three tables, because this component is the composition root above
		// every section (TR-7). A section that registered its own entry from inside its own lazy
		// chunk would have no switcher entry until that chunk loaded — while the switcher is the
		// only way to load it. The panel defaults are on the same hook for a sharper reason: the
		// store seeds a section the first time that section becomes active, and the very first
		// render already has one active, so a default registered any later than here is a default
		// the first entry never sees. Tabs are the opposite case and are registered by the
		// component the section renders under the outlet below.
		//
		// All three cross the `type:core` / application → `type:ui` seam as DATA, never as an
		// import in either direction: `AppSectionDefinition` is field-for-field assignable to the
		// registry's own `AppShellSection`, and the rail buttons and the panel defaults are
		// declared beside this component precisely so the library keeps naming none of them (TR-1).
		//
		// The rail buttons and the panel defaults are one-shot, as they have always been. The
		// SECTIONS are now derived, so they are registered twice over: once synchronously here, and
		// then again from the effect below on every later change. The synchronous call is the part
		// that is load-bearing — `AppShellPanelStore` seeds from the active section in a root-scoped
		// effect of its own, and the doc on its `seedFromActiveSection` states outright that
		// registration is "a composition-root, constructor-time action by contract, so the defaults
		// are always in place before this effect first runs". An effect created here first runs with
		// THIS component's change detection, which is later than this constructor and is not ordered
		// against a root service's effect, so moving the first registration into it would trade a
		// fact the store relies on for an assumption about flush order. The effect's own first run
		// reads the same cached `computed`, hence the same array reference, and registering it is a
		// no-op rather than a second write.
		this.registry.registerSections(untracked(() => this.switcherSections()));
		this.panelStore.registerRailButtons(APP_RAIL_BUTTONS);
		this.panelStore.registerPanelDefaults(PANEL_DEFAULTS);

		// One source of truth, re-registered whenever the URL, the project or the flag moves it: entering
		// a project points the entries at it, and the flag arriving turns Supply on without a navigation.
		effect(() => this.registry.registerSections(this.switcherSections()));

		this.closeNavigatorOnProjectPick();
		this.redirectFromUngrantedSurface();
	}

	/**
	 * Closes the overlaid panel when the reader clicks anywhere outside it. Push mode is left alone:
	 * that panel takes its own column, so a click in the page beside it is not a dismissal.
	 *
	 * The press and the release must both land outside: a `click` targets the common ancestor of the
	 * two, so a text selection dragged out of the panel would otherwise read as an outside click.
	 */
	protected closeOverlayPanelOnOutsideClick(event: MouseEvent): void {
		const hasPressStartedOutsidePanel = this.hasPressStartedOutsidePanel;
		this.hasPressStartedOutsidePanel = false;

		if (hasPressStartedOutsidePanel && this.isOutsideOverlayPanel(event)) {
			this.panelStore.close();
		}
	}

	protected rememberPressOrigin(event: PointerEvent): void {
		this.hasPressStartedOutsidePanel = this.isOutsideOverlayPanel(event);
	}

	/**
	 * Whether the event landed outside the open overlay panel. The rail is not outside: its buttons
	 * already toggle the panel. Neither is the section switcher. Neither is the CDK overlay container, where
	 * a menu or dialog opened from the panel renders. The event path is read instead of `contains()`: a
	 * click that re-renders the panel's content detaches its target before the document listener runs.
	 */
	private isOutsideOverlayPanel(event: Event): boolean {
		const overlayPanel = this.overlayPanel()?.nativeElement;

		if (!overlayPanel || this.panelRenderMode() !== APP_SHELL_PANEL_MODE.Overlay) {
			return false;
		}

		const rail = this.rail().nativeElement;
		const sectionSwitcher = this.sectionSwitcher().nativeElement;

		return !event
			.composedPath()
			.some(
				target =>
					target === overlayPanel ||
					target === rail ||
					target === sectionSwitcher ||
					(target instanceof Element && target.classList.contains(CDK_OVERLAY_CONTAINER_CLASS)),
			);
	}

	/**
	 * Dismisses the domain navigator once the reader has opened a project out of it.
	 *
	 * It is here rather than in a selection handler because the navigator has none to hook: the Clarity
	 * domain panel's PROJECTS rows are `routerLink`s, so the pick IS the navigation and nothing else
	 * observes it — a domain pick is the panel's own, and it closes the navigator itself.
	 *
	 * **A pick is a navigation that lands in a DIFFERENT project.** A tab click and a section switch
	 * leave the navigator alone, because neither changes the project.
	 */
	private closeNavigatorOnProjectPick(): void {
		let openedBeforeNavigation = false;
		let requestedProjectId: string | null = null;
		let projectIdBeforeNavigation: string | null = null;
		let isResumingRedirect = false;

		this.router.events
			.pipe(
				filter(
					event =>
						event instanceof NavigationStart ||
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationSkipped ||
						event instanceof NavigationError,
				),
				takeUntilDestroyed(),
			)
			.subscribe(event => {
				// A guard that answers with a `UrlTree` CANCELS this navigation and schedules another
				// one, already pointing at the project it chose. That second `NavigationStart` carries
				// a project the reader never named — the Design landing page redirects to the last
				// project this way — so the question stays the one the reader actually asked.
				if (event instanceof NavigationCancel) {
					isResumingRedirect = event.code === NavigationCancellationCode.Redirect;

					return;
				}

				// The redirect does not always produce that second navigation: one that resolves to the
				// URL already open is skipped, and one that fails errors out. Either way the flag has
				// done its job, and leaving it set would make the reader's NEXT navigation inherit it.
				if (event instanceof NavigationSkipped || event instanceof NavigationError) {
					isResumingRedirect = false;

					return;
				}

				if (event instanceof NavigationStart) {
					if (isResumingRedirect) {
						isResumingRedirect = false;

						return;
					}

					openedBeforeNavigation = this.navigator.isOpen();
					// The URL as ASKED FOR, before any redirect: a row in the navigator names the project
					// it opens, while a switcher jump names a section landing page and is sent on to a
					// project from there. Reading the final URL alone cannot tell those two apart.
					requestedProjectId = readProjectId(this.primarySegmentsOf(event.url));
					projectIdBeforeNavigation = readProjectId(this.primarySegmentsOf(this.router.url));

					return;
				}

				if (!openedBeforeNavigation || !this.navigator.isOpen()) {
					return;
				}

				if (requestedProjectId !== null && requestedProjectId !== projectIdBeforeNavigation) {
					this.navigator.close();
				}
			});
	}

	private redirectFromUngrantedSurface(): void {
		effect(() => {
			const surfaceId = this.openedProjectSurfaceId();

			if (surfaceId === null || this.surfaceAccess.grantedSurfaceIds().has(surfaceId)) {
				return;
			}

			const controlCenterUrl = this.readProjectControlCenterUrl();

			if (controlCenterUrl === null) {
				return;
			}

			untracked((): void => {
				void this.router.navigateByUrl(controlCenterUrl, { replaceUrl: true });
			});
		});
	}

	private readProjectControlCenterUrl(): string | null {
		const link = this.projectDomainLink.current();
		const domainId = link?.domainId ?? null;

		if (domainId !== null) {
			return `${URL_SEGMENT_SEPARATOR}${toControlCenterPath(domainId)}`;
		}

		return link?.isSettled === true ? `${URL_SEGMENT_SEPARATOR}${APP_ROUTES.CLARITY.path}` : null;
	}

	/**
	 * A URL's primary-outlet segments, read from the PARSED tree rather than from the string.
	 *
	 * A named outlet serializes into the URL in parentheses — the project's own comments outlet writes
	 * `(comments:chats)` — so splitting the raw string hands back a segment like `(summary` and reads
	 * as a different project. Opening the comments sidebar would then dismiss the navigator as if the
	 * reader had picked something. The primary outlet carries the route the reader is actually on.
	 */
	private primarySegmentsOf(url: string): readonly string[] {
		const primarySegments = this.router.parseUrl(url).root.children[PRIMARY_OUTLET]?.segments ?? [];

		return primarySegments.map(segment => segment.path);
	}
}
